import { FC, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  InputSelect,
  InputText,
  InputTextArea,
} from '@farmshare/ui-components';
import { useFormikContext } from 'formik';
import { ImageUploadInput } from './image-upload-input';
import { PricingFields } from './pricing-fields';
import type { Primal, Specification } from '../../types';

interface CutFormProps {
  primals?: Primal[];
  capabilityId: string;
  /** Cut-type specifications available for assignment to this cut */
  specifications?: Specification[];
}

/**
 * Renders children only when the active tab index matches.
 * @param root0
 * @param root0.value
 * @param root0.index
 * @param root0.children
 */
const TabPanel: FC<{
  value: number;
  index: number;
  children: React.ReactNode;
}> = ({ value, index, children }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

/**
 * Tabbed form for adding/editing a Cut.
 * Tabs: "Basic Details" | "Pricing"
 * Used with the useModal save pattern.
 * @param props - Component props
 * @param props.primals - List of primals for selection
 * @param props.capabilityId - Capability set ID for S3 image grouping
 * @param props.specifications - Cut-type specifications available for assignment
 * @returns Cut form component
 */
export const CutForm: FC<CutFormProps> = ({
  primals = [],
  capabilityId,
  specifications = [],
}) => {
  const [tab, setTab] = useState(0);
  const { values, setFieldValue } = useFormikContext<{
    specificationIds?: string[];
    [key: string]: unknown;
  }>();

  const primalOptions = primals.map((p) => ({
    value: p.id,
    label: p.title,
  }));

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, newTab) => setTab(newTab)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        <Tab label="Basic Details" />
        <Tab label="Pricing" />
        {specifications.length > 0 && <Tab label="Specifications" />}
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2}>
            <ImageUploadInput
              nameOverride="imageUrl"
              label="Image"
              capabilityId={capabilityId}
              avatar
            />
            <Stack spacing={2} flex={1}>
              <InputText label="Question Title" nameOverride="title" required />
              <InputTextArea
                label="Description"
                nameOverride="description"
                rows={2}
                hint="Optional description to help customers understand their options"
              />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2}>
            {primalOptions.length > 0 && (
              <InputSelect
                label="Primal Section"
                nameOverride="groupId"
                options={primalOptions}
                autoSelectFirstOption={false}
              />
            )}
            <InputText
              label="Printed Text"
              nameOverride="printedText"
              hint='Override how this item appears on the printed cutsheet (e.g. "1.5&apos;" instead of "1.5 inch")'
            />
          </Stack>
        </Stack>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <PricingFields />
      </TabPanel>

      {specifications.length > 0 && (
        <TabPanel value={tab} index={2}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Select which specifications apply to this cut. Only
              &ldquo;Cut-Specific&rdquo; specifications are shown.
            </Typography>
            <Autocomplete
              multiple
              options={specifications}
              getOptionLabel={(opt) => opt.title}
              value={specifications.filter((s) =>
                (values.specificationIds || []).includes(s.id),
              )}
              onChange={(_, newValue) =>
                setFieldValue(
                  'specificationIds',
                  newValue.map((s) => s.id),
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select specifications..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((opt, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={opt.title}
                      {...tagProps}
                      size="small"
                    />
                  );
                })
              }
            />
          </Stack>
        </TabPanel>
      )}
    </Box>
  );
};
